import { JSONContent } from '@tiptap/core';
import { gql } from 'apollo-angular';

// #738: Announcement-Verwaltung (Plattform-Broadcasts durch Nexato).
//
// Alle Operationen pruefen serverseitig selbst die Rolle framework_nexato_employee
// (requirePlatformRole()) — das Web-Gating ueber PermissionGuard ist Komfort, keine Sicherheitsgrenze.
//
// Die Ankuendigung erzeugt beim Fan-out fuer jeden Empfaenger eine ganz normale Notification. Es gibt
// keinen zweiten Zustellweg, nur eine andere Quelle.

/**
 * Lebenszyklus einer Ankuendigung.
 *
 * SCHEDULED --(Fan-out startet)--> RUNNING --> DONE
 *     |                               |          |
 *     |                               `----------+--(retractAnnouncement)--> RETRACTED
 *     `--(cancelAnnouncement)--> CANCELLED
 *
 * Kommt als roher String aus dem Schema (`status: String!`) und wird serverseitig mit
 * `AnnouncementStatus.valueOf(status)` geparst — also CASE-SENSITIV. Der Statusfilter darf
 * ausschliesslich diese Werte senden.
 */
export enum AnnouncementStatus {
  SCHEDULED = 'SCHEDULED',
  RUNNING = 'RUNNING',
  DONE = 'DONE',
  CANCELLED = 'CANCELLED',
  /**
   * Nach dem Fan-out zurueckgenommen (retractAnnouncement). Der Unterschied zu CANCELLED ist
   * nicht graduell: eine abgebrochene Ankuendigung war nie draussen, eine zurueckgezogene sehr
   * wohl. Zustellungen, die noch nicht raus waren, enden mit decisionReason
   * ANNOUNCEMENT_RETRACTED; bereits versendete E-Mails und Pushes bleiben, wo sie sind.
   */
  RETRACTED = 'RETRACTED'
}

/**
 * Fortschritt des Fan-outs — zaehlt MANDANTEN, nicht Empfaenger.
 *
 * Also „12 von 140 Mandanten", nicht „12 von 4.500 Nutzern". Wie viele Personen erreicht wurden, sagt
 * die API nicht; eine Reichweiten-Statistik ist ausdrueckliches Nicht-Ziel der Engine.
 *
 * totalTenants wird bei jeder Abfrage neu aufgeloest: kommt zwischenzeitlich ein Mandant hinzu,
 * verschiebt sich der Nenner. Kein festgeschriebener Wert.
 */
export interface FanoutProgress {
  completedTenants: number;
  totalTenants: number;
}

/**
 * Wie weit ein Rueckzug gekommen ist.
 *
 * Bewusst NICHT FanoutProgress wiederverwendet: „wie viele Mandanten haben es bekommen" und „wie
 * vielen wurde es genommen" sind verschiedene Fragen, die mitten im Durchlauf auseinanderliegen.
 *
 * NUR auf der Antwort von retractAnnouncement gesetzt — bei create/update/cancel und in der Liste
 * ist das Feld null (Schemakommentar in announcement.graphqls). Der Wert muss deshalb aus der
 * MUTATIONSANTWORT gehalten werden; ein Neuladen der Liste liefert ihn nicht.
 */
export interface RetractionProgress {
  retractedTenants: number;
  totalTenants: number;
}

/**
 * Inhalt einer Ankuendigung je Sprache.
 *
 * Serverseitig ein pass-through-Map-Scalar — das Schema sagt ueber die Struktur NICHTS. Diese
 * Interfaces sind die einzige Stelle, an der sie festgehalten ist.
 *
 * `de` ist Pflicht und braucht einen nicht-leeren `title`. `body` ist ein ProseMirror-Dokument
 * (Client-Dok §8.1), kein String — jede Sprache traegt ihren eigenen, unabhaengigen Baum. Der
 * Server prueft ihn gegen die Allow-List und lehnt beim Anlegen ab; die Allow-List steht in
 * announcement-rich-text.ts. Beim Rendern gilt exakte Uebereinstimmung, sonst Rueckfall
 * auf `de`.
 *
 * `body` ist NULLABLE — echt, nicht nur typografisch: `RichTextValidator` gibt fuer einen
 * null-Body sofort frei, denn nur der `de`-Titel ist Pflicht. Eine reine Titelmeldung
 * („Wartung heute 22:00") muss anlegbar bleiben, also traegt eine Sprache mit Titel, aber ohne
 * je angefassten Text, legitim `body: null` — sowohl in Serverantworten (Announcement) als auch
 * im Input (CreateAnnouncementInput), da beide dieses Interface teilen. NICHT durch ein
 * synthetisches leeres Dokument ersetzen.
 */
export interface AnnouncementContentEntry {
  title: string;
  body: JSONContent | null;
}

export type AnnouncementContent = Record<string, AnnouncementContentEntry>;

/**
 * Zielgruppe. Ebenfalls ein pass-through-Map-Scalar.
 *
 * GENAU EINES von beidem: `allTenants: true` ODER eine nicht-leere `tenantIds`-Liste. Der Server
 * prueft `allTenants() == hasTenantIds()` — beides gleichzeitig ist ein Fehler, keines von beidem
 * ebenfalls.
 *
 * tenantIds sind Mandanten-KURZCODES (Tenant.tenantId), keine UUIDs. Jeder Eintrag muss einen echten
 * Mandanten treffen, sonst ValidationProblem — die Pruefung existiert ausdruecklich, damit ein
 * Tippfehler nicht zu einer Ankuendigung mit null Empfaengern fuehrt, die als DONE endet.
 *
 * roles ist optional und wird NICHT validiert. Ein Tippfehler dort erreicht still niemand — deshalb
 * im Formular Auswahl aus getRoles, nie Freitext.
 */
export interface AnnouncementTargeting {
  allTenants?: boolean;
  tenantIds?: string[];
  roles?: string[];
}

export interface Announcement {
  id: string;
  typeKey: string;
  /**
   * Optimistische Sperre fuer updateAnnouncement. Der zuletzt gesehene Wert geht mit; ein
   * veralteter liefert einen Konfliktfehler statt eines stillen Ueberschreibens.
   *
   * Nach jedem erfolgreichen updateAnnouncement/retractAnnouncement den Wert AUS DER ANTWORT
   * uebernehmen, nie selbst hochzaehlen (Client-Dok §8.3).
   */
  version: number;
  /** Gesetzt, wenn diese Ankuendigung eine andere richtigstellt (Client-Dok §8.2), sonst null. */
  correctsAnnouncementId: string | null;
  content: AnnouncementContent;
  targeting: AnnouncementTargeting;
  scheduledAt: string;
  /** `null` = kein Banner (Client-Dok §8.6). Sonst der Zeitpunkt, bis zu dem sie dort erscheint. */
  dashboardUntil: string | null;
  /**
   * Externes Ziel (Client-Dok §8.7), sonst `null`. Nie zusammen mit einem `deepLink` gesetzt —
   * Ankuendigungen tragen ohnehin nie einen, das Ziel ist entweder extern (dieses Feld) oder gar
   * keins.
   */
  uriLink: string | null;
  /**
   * Client-Dok §8.3/§8.6: Loeschen wird serverseitig abgelehnt (auch in der Glocke), und ein
   * Dashboard-Banner ueberlebt das Lesen. Unabhaengig von dashboardUntil — ohne Fenster einfach ein
   * unloeschbarer Glockeneintrag, den nur die TTL begrenzt.
   */
  sticky: boolean;
  status: string;
  fanoutProgress: FanoutProgress;
  /** Siehe RetractionProgress: NUR auf der Antwort von retractAnnouncement gesetzt. */
  retractionProgress?: RetractionProgress | null;
  createdBy: string;
  createdAt: string;
  updatedAt: string | null;
}

/** Cursor-Seite. Kein totalCount — bewusst, siehe Spec. */
export interface AnnouncementPage {
  items: Announcement[];
  nextCursor: string | null;
}

/**
 * Eingabe fuer createAnnouncement und sendTestAnnouncement.
 *
 * scheduledAt ist optional und wird bei „mit Vorlauf senden" GAR NICHT gesetzt — nur dann greift der
 * Server-Vorlauf von 5 Minuten (Property notification-engine.announcement.min-lead), und nur der ist
 * die Reissleine, in der sich die Ankuendigung noch abbrechen laesst. Mit Angabe: ISO-8601-Instant
 * mit `Z`; ein anderes Format erzeugt eine ValidationProblem.
 */
export interface CreateAnnouncementInput {
  /**
   * Idempotenzschluessel. Im Schema nullable, weil sendTestAnnouncement denselben Input teilt und
   * nichts persistiert — fuer createAnnouncement und correctAnnouncement aber PFLICHT: beide
   * werfen sonst ValidationProblem („clientRequestId must not be null",
   * AnnouncementAdminServiceImpl:137 bzw. :271).
   *
   * EINMAL JE ABSENDEABSICHT, erzeugt beim Oeffnen der Maske, bei jedem Wiederholungsversuch
   * UNVERAENDERT (Client-Dok §8.3). Wer bei jedem Klick neu wuerfelt, hat ein Pflichtfeld
   * ausgefuellt und keinen Schutz gewonnen.
   *
   * Ein zweiter Aufruf mit demselben Schluessel und demselben Inhalt liefert die bereits
   * angelegte Ankuendigung zurueck — genau das braucht ein Retry nach einem Timeout. Weicht der
   * Inhalt ab, lehnt der Server ab: das ist kein Netzwerkfall mehr, sondern ein Admin, der nach
   * einem entdeckten Tippfehler das Formular geaendert und erneut gesendet hat.
   */
  clientRequestId?: string;
  typeKey: string;
  content: AnnouncementContent;
  targeting: AnnouncementTargeting;
  scheduledAt?: string;
  /**
   * Client-Dok §8.3: optional, muss NACH `scheduledAt` liegen und die Lebensdauer der Nachricht
   * nicht ueberschreiten (7 Tage `announcement.maintenance`, 30 Tage `announcement.product`).
   * Weggelassen = kein Dashboard-Banner.
   */
  dashboardUntil?: string;
  /**
   * Client-Dok §8.7: optional, absolute `http`/`https`-URL, kein `mailto`, hoechstens 2048 Zeichen.
   * Weggelassen = kein externes Ziel. Zaehlt beim Idempotenzvergleich mit — anders als
   * `scheduledAt`/`dashboardUntil` lehnt der Server einen Wiederholungsversuch mit derselben
   * `clientRequestId`, aber geaenderter Adresse ab.
   */
  uriLink?: string;
  /**
   * Client-Dok §8.3: optional, unabhaengig von `dashboardUntil`. Mit Fenster sollte die Oberflaeche
   * ein kurzes vorbelegen (ein bis zwei Tage) — ein unaufhebbares Banner, das eine Woche steht,
   * wird zur Tapete.
   */
  sticky?: boolean;
}

/**
 * Eingabe fuer updateAnnouncement.
 *
 * `content` geht IMMER mit — der Server ersetzt es und schreibt das alte in die
 * Revisionshistorie (AnnouncementRevision).
 *
 * `targeting` und `scheduledAt` sind nur erlaubt, solange die Ankuendigung noch SCHEDULED ist;
 * danach lehnt der Server sie ab („targeting and scheduledAt are frozen once fan-out started").
 * Nach dem Fan-out also WEGLASSEN, nicht als null senden.
 *
 * `reason` landet in der Revisionshistorie und beantwortet spaeter „warum stand da vorher etwas
 * anderes". Optional — anders als beim Rueckzug, dessen Grund nur ins Serverlog geht.
 */
export interface UpdateAnnouncementInput {
  version: number;
  content: AnnouncementContent;
  targeting?: AnnouncementTargeting;
  scheduledAt?: string;
  /**
   * Anders als `targeting`/`scheduledAt` auch NACH dem Fan-out aenderbar (Client-Dok §8.3).
   * Dreiwertig, deshalb hier IMMER explizit gesendet, nie weggelassen: ein Zeitstempel setzt das
   * Banner (oder bestaetigt den unveraenderten Stand erneut), ein LEERER STRING nimmt es ab.
   */
  dashboardUntil: string;
  /**
   * Wie `dashboardUntil` auch nach dem Fan-out aenderbar und deshalb IMMER explizit gesendet
   * (Client-Dok §8.7): eine URL setzt/aendert das Ziel, ein leerer String nimmt es ab.
   */
  uriLink: string;
  /** Wie beim Anlegen unabhaengig von `dashboardUntil` — ein normales Boolean, keine Dreiwertigkeit. */
  sticky: boolean;
  reason?: string;
}

export interface AnnouncementPageResponse {
  announcements: AnnouncementPage;
}

export interface CreateAnnouncementResponse {
  createAnnouncement: Announcement;
}

export interface CancelAnnouncementResponse {
  cancelAnnouncement: Announcement;
}

export interface UpdateAnnouncementResponse {
  updateAnnouncement: Announcement;
}

export interface RetractAnnouncementResponse {
  retractAnnouncement: Announcement;
}

export interface CorrectAnnouncementResponse {
  correctAnnouncement: Announcement;
}

export interface SendTestAnnouncementResponse {
  sendTestAnnouncement: boolean;
}

/**
 * OHNE retractionProgress: das Feld ist auf jeder anderen Antwort null, und ein Fragment, das
 * ueberall dasselbe null mitschleppt, verschleiert, dass es genau einer Antwort gehoert. Die
 * Rueckzugs-Mutation waehlt es zusaetzlich zu diesem Block.
 */
const ANNOUNCEMENT_FIELDS = `
  id
  typeKey
  version
  correctsAnnouncementId
  content
  targeting
  scheduledAt
  dashboardUntil
  uriLink
  sticky
  status
  fanoutProgress {
    completedTenants
    totalTenants
  }
  createdBy
  createdAt
  updatedAt
`;

/**
 * status ist optional; leer bedeutet „alle" und wird deshalb WEGGELASSEN, nicht als null gesendet.
 * Ein gesetzter Wert muss exakt einer der vier Enum-Namen sein (AnnouncementStatus.valueOf).
 *
 * Standard-limit 20, kein totalCount — deshalb „Mehr laden" statt Seitenzahlen.
 */
export const ANNOUNCEMENTS_QUERY = gql`
  query announcements($status: String, $cursor: ID, $limit: Int!) {
    announcements(status: $status, cursor: $cursor, limit: $limit) {
      items {
        ${ANNOUNCEMENT_FIELDS}
      }
      nextCursor
    }
  }
`;

export const CREATE_ANNOUNCEMENT_MUTATION = gql`
  mutation createAnnouncement($input: CreateAnnouncementInput!) {
    createAnnouncement(input: $input) {
      ${ANNOUNCEMENT_FIELDS}
    }
  }
`;

/**
 * Abbrechen geht NUR solange SCHEDULED. `cancelIfScheduled` ist ein bedingtes Update; trifft es keine
 * Zeile, wirft der Server ValidationProblem. Das ist ein Wettlauf: die Liste kann SCHEDULED zeigen,
 * waehrend der Fan-out laengst laeuft. Der Fehlerfall braucht eine verstaendliche Meldung plus ein
 * Neuladen — nicht den englischen Server-Text.
 *
 * Fuer eine bereits laufende oder abgeschlossene Ankuendigung ist RETRACT_ANNOUNCEMENT_MUTATION
 * das Gegenstueck; die beiden teilen sich den Lebenszyklus und ueberschneiden sich nicht.
 */
export const CANCEL_ANNOUNCEMENT_MUTATION = gql`
  mutation cancelAnnouncement($id: ID!) {
    cancelAnnouncement(id: $id) {
      ${ANNOUNCEMENT_FIELDS}
    }
  }
`;

/**
 * Inhalt einer bestehenden Ankuendigung aendern — moeglich in SCHEDULED, RUNNING und DONE, nicht
 * mehr in CANCELLED/RETRACTED („announcement is not editable in status …").
 *
 * `version` ist die Sperre. Ein veralteter Wert liefert „announcement was changed by someone
 * else"; das bedeutet IMMER, dass der eigene Listenstand veraltet ist — also Meldung plus
 * Neuladen, nicht nur ein Fehlertoast.
 */
export const UPDATE_ANNOUNCEMENT_MUTATION = gql`
  mutation updateAnnouncement($id: ID!, $input: UpdateAnnouncementInput!) {
    updateAnnouncement(id: $id, input: $input) {
      ${ANNOUNCEMENT_FIELDS}
    }
  }
`;

/**
 * RUNNING/DONE --> RETRACTED. SCHEDULED gehoert nicht hierher, dafuer ist cancelAnnouncement da.
 *
 * Ein erneuter Aufruf auf einer bereits RETRACTED-Ankuendigung ist KEIN Fehler, sondern die
 * WIEDERAUFNAHME eines unvollstaendigen Durchlaufs: es gibt keinen Hintergrundjob, der einen
 * abgebrochenen Lauf nachholt (Client-Dok §8.3). Ein Wiederaufnahme-Aufruf laesst version und
 * updatedAt absichtlich unberuehrt — sonst behauptete die Liste eine Aenderung, die es nicht gab.
 *
 * retractionProgress steht NUR hier. Zeigt es weniger als totalTenants, gehoert ein sichtbarer,
 * STEHENDER Hinweis in die Oberflaeche — ein Toast ist weg, bevor jemand „Erneut versuchen"
 * druecken kann.
 */
export const RETRACT_ANNOUNCEMENT_MUTATION = gql`
  mutation retractAnnouncement($id: ID!, $reason: String) {
    retractAnnouncement(id: $id, reason: $reason) {
      ${ANNOUNCEMENT_FIELDS}
      retractionProgress {
        retractedTenants
        totalTenants
      }
    }
  }
`;

/**
 * Legt eine NEUE, ganz normale Ankuendigung an, die die referenzierte richtigstellt — eigener
 * Fan-out, eigener Lebenszyklus, eigene Zeile. Nur moeglich, solange das Original RUNNING oder
 * DONE ist: eine zurueckgezogene Ankuendigung laesst sich nicht mehr richtigstellen, und das ist
 * eine Einbahnstrasse, die die Oberflaeche VOR dem Rueckzug ansprechen muss.
 *
 * `input.typeKey` und `input.targeting` gehen mit, weil der Typ sie verlangt — ihr WERT wird
 * serverseitig ignoriert: der Typ kommt vom Original, die Zielgruppe wird aus dessen
 * Fan-out-Zeilen aufgeloest. Deshalb sind beide Felder im Formular ausgeblendet und nicht
 * bedienbar.
 *
 * Der Idempotenzvergleich ist hier ein anderer als bei createAnnouncement: verglichen werden die
 * Id des Originals und `content`.
 */
export const CORRECT_ANNOUNCEMENT_MUTATION = gql`
  mutation correctAnnouncement($id: ID!, $input: CreateAnnouncementInput!) {
    correctAnnouncement(id: $id, input: $input) {
      ${ANNOUNCEMENT_FIELDS}
    }
  }
`;

/**
 * Schickt den Entwurf NUR an den Aufrufer und legt NICHTS an — es entsteht kein Datensatz in der
 * Liste. Geprueft werden content und typeKey, aber NICHT targeting: ein Entwurf laesst sich proben,
 * bevor die Zielgruppe steht.
 *
 * locale erzwingt die Rendersprache unabhaengig von der eigenen Spracheinstellung; fehlt die Sprache
 * im Content, greift der de-Rueckfall.
 *
 * Es laeuft durch die normale Pipeline: die eigene E-Mail bzw. Push geht wirklich raus.
 *
 * NICHT zu verwechseln mit sendTestNotification(typeKey) aus den Profil-Einstellungen (#735) — die
 * schickt den sampleContext() des Typs, also einen festen Beispieltext aus dem Code, nicht diesen
 * Entwurf.
 */
export const SEND_TEST_ANNOUNCEMENT_MUTATION = gql`
  mutation sendTestAnnouncement($input: CreateAnnouncementInput!, $locale: String) {
    sendTestAnnouncement(input: $input, locale: $locale)
  }
`;
